import Typography from '@mui/material/Typography';
import { Trans, useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import {
    ExtraCategoriesDocument,
    IContentTypeValue,
    ICertificateEditFragment,
    ScoringSystemsDocument,
} from 'graphql/types';
import { TextField, ScoringField, SwitchField } from 'common/forms/Field';
import { Divider } from 'common/components/Divider';
import { FormSection } from 'common/components/FormSection';
import { FormFooter } from 'common/forms/components/FormFooter';
import { NotificationSection } from 'certificate/forms/CertificateForm/NotificationSection';
import { ExtraCategoriesSection } from 'certificate/forms/CertificateForm/ExtraCategoriesSection';
import { getExtraCategoryInitialFormValues } from 'organisation/utils/extraCategory';
import { Link } from 'common/components/Link';
import {
    EScoringSystemOption,
    TExternalCertificateFormValues,
} from 'certificate/types';
import { getExternalCertificateFormValidator } from 'certificate/forms/validators/externalCertificateForm';

interface IProps {
    certificate?: ICertificateEditFragment;
    onCreate: (
        values: TExternalCertificateFormValues,
        isExternal?: boolean
    ) => void | Promise<void>;
    onUpdate?: SubmitHandler<TExternalCertificateFormValues>;
    onDelete?(): void | Promise<void>;
}

export const ExternalCertificateForm = ({
    certificate,
    onCreate,
    onUpdate,
    onDelete,
}: IProps) => {
    const [translate] = useTranslation();
    const { data: scoringSystemsData } = useQuery(ScoringSystemsDocument);

    const isEdit = !!certificate;

    const { data: extraCategoriesData } = useQuery(ExtraCategoriesDocument, {
        variables: { contentType: IContentTypeValue.Certificate },
    });

    const defaultValues = useMemo<TExternalCertificateFormValues>(
        () => ({
            name: certificate?.name || '',
            sendNotification: certificate?.sendNotification ?? false,
            notificationOffset: certificate?.notificationOffset || undefined,
            notificationOffsetScale:
                certificate?.notificationOffsetScale || undefined,
            notificationTargets: certificate?.notificationTargets || [],
            scoring: {
                score: certificate?.scoring?.[0]?.score ?? undefined,
                scoringSystemId:
                    certificate?.scoring?.[0]?.scoringSystem?.id ??
                    EScoringSystemOption.NoScoringSystem,
            },
            requiresValidation: certificate?.requiresValidation ?? false,
            reference: certificate?.ref || undefined,
            extraCategories: getExtraCategoryInitialFormValues(
                extraCategoriesData?.extraCategories,
                certificate?.extraCategoryValues
            ),
        }),
        [certificate, extraCategoriesData?.extraCategories]
    );

    const formMethods = useForm<TExternalCertificateFormValues>({
        defaultValues,
        resolver: zodResolver(getExternalCertificateFormValidator()),
    });

    const { control, handleSubmit, reset } = formMethods;

    // Reset form when certificate data changes
    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    const handleFormSubmit: SubmitHandler<TExternalCertificateFormValues> =
        isEdit && onUpdate ? onUpdate : (values) => onCreate(values, true);

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <Typography gutterBottom variant="h3">
                    {translate('certificateForm.externalCertificate')}
                </Typography>

                <Typography sx={{ mb: 2 }}>
                    {translate(
                        'certificateForm.externalCertificateDescription'
                    )}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <TextField
                    control={control}
                    label={translate('name')}
                    name="name"
                />

                <Divider sx={{ mt: 3 }} />

                <FormSection
                    renderExpanded
                    description={translate(
                        'certificateForm.validationDescription'
                    )}
                    title={translate('certificateForm.validationTitle')}
                >
                    <SwitchField
                        control={control}
                        formControlProps={{ sx: { mb: 0 } }}
                        label={translate('certificateForm.requiresValidation')}
                        name="requiresValidation"
                    />
                </FormSection>

                {!!scoringSystemsData?.scoringSystems?.length && (
                    <FormSection
                        renderExpanded
                        description={translate(
                            'certificateForm.settingsDescription'
                        )}
                        title={translate('certificateForm.settingsTitle')}
                    >
                        <ScoringField control={control} name="scoring" />
                    </FormSection>
                )}

                <NotificationSection hideSendAchievedMail control={control} />

                <FormSection
                    description={
                        <Trans
                            components={{
                                a: <Link rel="noreferrer" underline="always" />,
                            }}
                            i18nKey="certificateForm.connectionsDescription"
                        />
                    }
                    title={translate('certificateForm.connectionsTitle')}
                >
                    <TextField
                        control={control}
                        label={translate('certificateForm.reference')}
                        name="reference"
                    />
                </FormSection>

                <ExtraCategoriesSection control={control} />

                <FormFooter
                    canDelete={!!onDelete}
                    deleteDialogText={translate(
                        'certificateForm.deleteDialogText'
                    )}
                    deleteDialogTitle={translate(
                        'certificateForm.deleteDialogTitle'
                    )}
                    onDelete={onDelete}
                />
            </form>
        </FormProvider>
    );
};
